/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useU2000GoogleExchange from "../../u2000Core/hooks/authentification/U2000UseGoogleExchange";
import useU2000SelectOrganization from "../../u2000Core/hooks/authentification/U2000UseSelectOrganization";
import { u2000Menu } from "../../u2000Core/menu/U2000Menu";
import { U2000MenuFindModuleFromPath } from "../../u2000Core/menu/U2000MenuUtils";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { NS } from "../U2001";
import { applyAuthenticatedLoginResponse, type U2001LoginLocationState } from "./U2001Login";

type Organization = {
  uuid: string;
  organizationName: string;
};

type LoginResponse = {
  isAuthenticated: boolean;
  changePasswordOnLogin?: boolean;
  organizations?: Organization[];
  token?: string;
};

type PendingAutoSelection = {
  organizations: Organization[];
  persistedOrganization: any;
};

/**
 * BL-0520 — Route publique `/login/google/return`.
 * Traite exchangeCode / sso=cancelled / ssoError=* puis rejoint le pipeline session.
 */
const U2001GoogleReturn = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const doGoogleExchange = useU2000GoogleExchange();
  const doSelectOrganization = useU2000SelectOrganization();
  const { setCurrentModule, markAsNewLogin, auth } = useU2000AuthContext();
  const toast = useToast();
  const started = useRef(false);

  const [statusMsg, setStatusMsg] = useState(t("login.sso.returning", { ns: NS }));
  const [pendingAutoSelection, setPendingAutoSelection] = useState<PendingAutoSelection | null>(null);
  const [, setOrganizations] = useState<Organization[]>([]);

  const goToHomePage = () => {
    markAsNewLogin();
    const currentModule = U2000MenuFindModuleFromPath(u2000Menu, "/U3400");
    setCurrentModule(currentModule);
    navigate("/", { replace: true });
  };

  const goToLogin = (state?: U2001LoginLocationState) => {
    navigate("/U2001", { replace: true, state: state ?? null });
  };

  const setScreenStateFn = (state: string) => {
    if (state === "changePassword") {
      goToLogin({ screenState: "changePassword" });
    }
    // selectOrganization : navigation via setOrganizationsAndMaybeNavigate
  };

  const setOrganizationsAndMaybeNavigate: Dispatch<SetStateAction<Organization[]>> = (value) => {
    setOrganizations((prev) => {
      const next = typeof value === "function" ? value(prev) : value;
      if (next.length > 1) {
        navigate("/U2001", {
          replace: true,
          state: { screenState: "selectOrganization", organizations: next } satisfies U2001LoginLocationState,
        });
      }
      return next;
    });
  };

  useEffect(() => {
    if (pendingAutoSelection && auth.token && auth.isAuthenticated) {
      const { organizations, persistedOrganization } = pendingAutoSelection;
      const handleAutoSelection = async () => {
        try {
          const orgResponse = await doSelectOrganization(persistedOrganization.uuid, u2000Menu);
          if (orgResponse.isAuthenticated) {
            toast.success(
              t("organizationAutoSelected", {
                organizationName: persistedOrganization.organizationName,
                ns: NS,
              }),
              t("successTitle", { ns: "common" }),
              3000,
            );
            goToHomePage();
          }
        } catch {
          toast.warning(t("autoSelectFailed", { ns: NS }), t("warningTitle", { ns: "common" }), 4000);
          navigate("/U2001", {
            replace: true,
            state: { screenState: "selectOrganization", organizations } satisfies U2001LoginLocationState,
          });
        } finally {
          setPendingAutoSelection(null);
        }
      };
      handleAutoSelection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.token, auth.isAuthenticated, pendingAutoSelection]);

  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;

    const cancelled = searchParams.get("sso") === "cancelled";
    const ssoError = searchParams.get("ssoError");
    const exchangeCode = searchParams.get("exchangeCode");

    if (cancelled) {
      goToLogin();
      return;
    }

    if (ssoError) {
      goToLogin({ ssoError });
      return;
    }

    if (!exchangeCode) {
      goToLogin({ ssoError: "EXPIRED_OR_REPLAY" });
      return;
    }

    const runExchange = async () => {
      try {
        const response: LoginResponse = await doGoogleExchange(exchangeCode, u2000Menu);
        if (response.isAuthenticated) {
          applyAuthenticatedLoginResponse(response, {
            setScreenStateFn,
            setOrganizations: setOrganizationsAndMaybeNavigate,
            goToHomePageFn: goToHomePage,
            setPendingAutoSelection,
          });
        } else {
          goToLogin({ ssoError: "EXPIRED_OR_REPLAY" });
        }
      } catch (error: any) {
        if (error?.status === 400) {
          const errorList = error?.data?.errors || [];
          const isExpired = errorList.some((e: { errorCode?: string }) => e.errorCode === "BU200102");
          goToLogin({ ssoError: isExpired ? "EXPIRED_OR_REPLAY" : "EXPIRED_OR_REPLAY" });
          return;
        }
        if (error?.status === 401) {
          const errorList = error?.data?.errors || [];
          const isLocked = errorList.some((e: { errorCode?: string }) => e.errorCode === "A0003");
          goToLogin({ ssoError: isLocked ? "USER_LOCKED" : "EXPIRED_OR_REPLAY" });
          return;
        }
        setStatusMsg(t("msgUnexpectedError", { ns: NS }));
        goToLogin();
      }
    };

    runExchange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-6 py-20">
      <div className="mx-auto flex w-full max-w-xl flex-col gap-4 rounded-2xl bg-gray-200 p-8 shadow-xs text-center">
        <div className="text-lg font-semibold text-gray-900">{statusMsg}</div>
      </div>
    </div>
  );
};

export default U2001GoogleReturn;
